// Phase 12 — Reusable quick-add form for read-only module routes.
// Usage: <QuickAdd label="New note" fields={[{ key: 'title', placeholder: 'Title', type: 'text' }]} endpoint="/api/modules/notes/entries" queryKey={['notes','entries']} />

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './Button.js';
import { Input } from './Input.js';

interface FieldDef { key: string; placeholder: string; type?: 'text' | 'date'; }

export function QuickAdd({ label, fields, endpoint, queryKey }: {
  label: string;
  fields: FieldDef[];
  endpoint: string;
  queryKey: string[];
}) {
  const [open, setOpen] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (body: Record<string, string>) => {
      const res = await fetch(endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setOpen(false);
      formRef.current?.reset();
    },
  });

  if (!open) {
    return <Button variant="ghost" onClick={() => setOpen(true)} className="text-accent-default">{label}</Button>;
  }

  return (
    <form ref={formRef} onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); const body: Record<string,string>={}; fields.forEach(f => { const v = fd.get(f.key); if(v) body[f.key]=v.toString(); }); mutation.mutate(body); }}
      className="flex flex-wrap items-end gap-2 rounded-lg border border-border-subtle bg-surface-raised p-3"
    >
      {fields.map(f => (
        <Input key={f.key} name={f.key} placeholder={f.placeholder} type={f.type??'text'} className="flex-1 min-w-[120px]" />
      ))}
      <Button type="submit" variant="primary" size="sm" disabled={mutation.isPending}>Save</Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
    </form>
  );
}
